import type {Booking, Option, OptionContent} from '@/lib/flight/flightModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import checkoutStyle from '@/views/Flight/Checkout.module.scss'

import style from './DisplayOptionContent.module.scss'

export function DisplayOptionContent({
  optionContent,
  optionContentTitle,
}: {
  optionContent: OptionContent | undefined
  optionContentTitle: string
}) {
  if (
    (optionContent?.baggage_prices ?? []).length === 0 &&
    (optionContent?.extensions ?? []).length === 0
  ) {
    return null
  }

  return (
    <>
      <h3 className={style.OptionContentTitle}>{optionContentTitle}</h3>
      {optionContent?.extensions && optionContent.extensions.length > 0 && (
        <>
          <h4 className={style.OptionContentSectionTitle}>What is included</h4>
          <ul className={style.OptionContentSectionList}>
            {optionContent.extensions.map(extension => (
              <li key={extension} className={style.OptionContentSectionBulletPoints}>
                {extension}
              </li>
            ))}
          </ul>
        </>
      )}

      {optionContent?.baggage_prices && optionContent.baggage_prices.length > 0 && (
        <div>
          <h4 className={style.OptionContentSectionTitle}>Baggage Allowance</h4>
          <ul className={style.OptionContentSectionList}>
            {optionContent.baggage_prices.map(baggagePrice => {
              return (
                <li key={baggagePrice} className={style.OptionContentSectionBulletPoints}>
                  {baggagePrice}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </>
  )
}

// This is to display new flight details using the new nested option object
export function DisplayMoreInfoOption({option}: {option: Option | undefined}) {
  const optionTitle = option?.option_title.trim() ?? 'Not available'

  return (
    <div className={checkoutStyle.BookingAdditionalInfo}>
      <h3>Fare Details</h3>
      <div className={checkoutStyle.BookingAdditionalInfoContent}>
        <div className={checkoutStyle.BookingAdditionalInfoItem}>
          <div className={checkoutStyle.BookingAdditionalInfoLabel}>Fare Type</div>
          <div className={checkoutStyle.BookingAdditionalInfoValue}>{optionTitle}</div>
        </div>
      </div>

      <DisplayOptionContent optionContent={option?.together} optionContentTitle='Together' />
      <DisplayOptionContent optionContent={option?.departing} optionContentTitle='Departing' />
      <DisplayOptionContent optionContent={option?.returning} optionContentTitle='Returning' />
    </div>
  )
}

// This is to display old flight details using the old moreInfo object API schema
export function DisplayMoreInfoOld({booking}: {booking: Booking}) {
  const optionTitle =
    booking.moreInfo.optionTitle?.trim() ??
    booking.flightSegments[0]?.flights[0]?.travel_class ??
    'Not available'

  return (
    <>
      <div className={checkoutStyle.BookingAdditionalInfo}>
        <h3>Fare Details</h3>
        <div className={checkoutStyle.BookingAdditionalInfoContent}>
          <div className={checkoutStyle.BookingAdditionalInfoItem}>
            <div className={checkoutStyle.BookingAdditionalInfoLabel}>Fare Type</div>
            <div className={checkoutStyle.BookingAdditionalInfoValue}>{optionTitle}</div>
          </div>
          {/* Extensions Section */}
          {booking.moreInfo.extensions?.map(extension => (
            <div key={extension} className={checkoutStyle.BookingAdditionalInfoItem}>
              <div className={checkoutStyle.BookingAdditionalInfoLabel}>{extension}</div>
              <div className={checkoutStyle.BookingAdditionalInfoValue}>
                <VisuallyHidden>Yes</VisuallyHidden>
                <span aria-hidden='true'>✓</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Baggage Prices Section */}
      {booking.moreInfo.baggagePrices && booking.moreInfo.baggagePrices.length > 0 && (
        <div className={checkoutStyle.BookingAdditionalInfo}>
          <h3>Baggage Prices</h3>
          <div className={checkoutStyle.BookingAdditionalInfoContent}>
            {booking.moreInfo.baggagePrices.map(baggagePrice => {
              const isIncluded = baggagePrice.toLowerCase().includes('free')
              const parts = baggagePrice.split(':')
              const label = parts[0]
              let value = parts.length > 1 ? parts.slice(1).join(':').trim() : ''

              if (isIncluded && !value) {
                value = 'Included'
              }

              return (
                <div key={baggagePrice} className={checkoutStyle.BookingAdditionalInfoItem}>
                  <div className={checkoutStyle.BookingAdditionalInfoLabel}>{label}</div>
                  {value && (
                    <div
                      className={clsx(
                        checkoutStyle.BookingAdditionalInfoValue,
                        isIncluded && checkoutStyle.BookingAdditionalInfoValueIncluded,
                      )}
                    >
                      {value}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
